<template>
  <CCard>
    <CCardHeader>
      <h2>取引先更新</h2>
    </CCardHeader>
    <CCardBody>
      <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>
      <div v-if="loadingInitial" class="d-flex align-items-center py-4">
        <CSpinner color="primary" class="mr-2"/>
        <span>取引先の更新内容を読み込んでいます。</span>
      </div>
      <template v-else-if="clientId">
        <p><span class="preika-required-note"><span class="preika-required-note__mark" aria-hidden="true">*</span><span>が付いている項目は必須入力項目です</span></span></p>
        <ValidationObserver ref="obs">
          <CRow v-if="message.length">
            <ul>
              <li class="preika-form-message" v-for="error in message" :key="error">{{ error }}</li>
            </ul>
          </CRow>
          <CRow>
            <CCol sm="3">
              <ValidationProvider name="取引先企業名" rules="required|max:100" v-slot="ProviderProps">
                <label>取引先企業名<span class="asterisk">*</span></label>
                <CInput v-model="company_name" />
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="3">
              <ValidationProvider name="取引先担当者名" rules="max:60" v-slot="ProviderProps">
                <CInput label="取引先担当者名" v-model="staff_name" />
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="2">
              <ValidationProvider name="電話番号" rules="tel" v-slot="ProviderProps">
                <CInput label="電話番号" v-model="telephone" description="ハイフンなしで入力してください" />
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="6">
              <ValidationProvider name="住所" rules="max:60" v-slot="ProviderProps">
                <CInput label="住所" v-model="address"/>
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="3">
              <ValidationProvider name="E-Mail" rules="email" v-slot="ProviderProps">
                <CInput label="E-Mail" v-model="email" placeholder="email@example.com"/>
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="2">
              <ValidationProvider name="支払いサイト" rules="numeric|max:3" v-slot="ProviderProps">
                <CInput label="支払いサイト" v-model="usance" />
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="6">
              <ValidationProvider name="備考" rules="max:254" v-slot="ProviderProps">
                <CInput label="備考" v-model="remarks" />
                <p class="error">{{ ProviderProps.errors[0] }}</p>
              </ValidationProvider>
            </CCol>
          </CRow>
          <br>
          <CRow class="mt-3 align-items-center">
            <CCol class="text-start">
              <button class="btn btn-secondary" @click="valueCheck">戻る</button>
            </CCol>
            <CCol class="text-center">
              <button @click="whenValid('obs', openModal)" class="btn btn-info" :disabled="loading">確認</button>
              <ClientUpdateModal
                :draggable="true"
                :resizable="true"
                :company_name="company_name"
                :staff_name="staff_name"
                :telephone="telephone"
                :address="address"
                :email="email"
                :usance="usance"
                :remarks="remarks"
                :loading="loading"
                :error="updateError"
                v-show="showModal"
                @update="update"
                @close="closeModal"
              />
            </CCol>
            <CCol></CCol>
          </CRow>
        </ValidationObserver>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import submitWhenValidMixin from '@/mixins/submitWhenValidMixin'
import axios from 'axios'
import ConstList from './../ConstList'
import ClientUpdateModal from './ClientUpdateModal'
import { recordQueryRoute, resolveRouteRecordId } from '@/common/routeRecordId'
import { safeErrorDigest } from '../utils/safeResponseMessage'

export default {
  mixins: [submitWhenValidMixin],
  name: 'ClientUpdate',
  components: { ClientUpdateModal },
  data () {
    return {
      clientId: null,
      showModal: false,
      company_name: '',
      staff_name: '',
      telephone: '',
      address: '',
      email: '',
      usance: '',
      remarks: '',
      message: [],
      loading: false,
      loadingInitial: false,
      // 読み込みの失敗だけ（画面の上端に出す）。
      errorMessage: '',
      // 更新を断られた理由。確認の小窓を閉じずに、小窓の中に出す。
      updateError: '',
      original: null
    }
  },
  watch: {
    '$route.fullPath': 'loadClient'
  },
  mounted () {
    this.loadClient()
  },
  methods: {
    async loadClient () {
      this.errorMessage = ''
      this.clientId = resolveRouteRecordId(this.$route)
      if (!this.clientId) {
        this.errorMessage = '取引先を特定できません。一覧から選択し直してください。'
        return
      }

      this.loadingInitial = true
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ORIGIN}/clients/${this.clientId}`
        )
        if (!response.data) {
          this.errorMessage = '取引先が見つかりません。画面を再読み込みしてください。'
          return
        }
        this.applyClient(response.data)
      } catch (error) {
        this.errorMessage = '取引先の更新内容を取得できませんでした。画面を再読み込みしてください。'
        console.error('クライアント更新取得失敗:', safeErrorDigest(error))
      } finally {
        this.loadingInitial = false
      }
    },
    applyClient (value) {
      this.company_name = value.company_name || ''
      this.staff_name = value.staff_name || ''
      this.telephone = value.telephone || ''
      this.address = value.address || ''
      this.email = value.email || ''
      this.usance = value.usance == null ? '' : String(value.usance)
      this.remarks = value.remarks || ''
      this.original = this.currentValues()
    },
    currentValues () {
      return {
        company_name: this.company_name,
        staff_name: this.staff_name,
        telephone: this.telephone,
        address: this.address,
        email: this.email,
        usance: String(this.usance == null ? '' : this.usance),
        remarks: this.remarks
      }
    },
    isDirty () {
      return this.original && JSON.stringify(this.currentValues()) !== JSON.stringify(this.original)
    },
    valueCheck () {
      if (this.isDirty() && !confirm(ConstList.TRANSITION)) return
      this.$router.push(recordQueryRoute(ConstList.ROUTE_CLIENT_INFO, this.clientId))
    },
    openModal () {
      this.updateError = ''
      this.showModal = true
    },
    async update () {
      this.loading = true
      this.updateError = ''
      try {
        await axios.post(
          `${process.env.VUE_APP_API_ORIGIN}/client/update`,
          {
            id: this.clientId,
            company_name: this.company_name,
            staff_name: this.staff_name,
            telephone: this.telephone,
            address: this.address,
            email: this.email,
            usance: this.usance,
            remarks: this.remarks
          },
          { headers: { 'Content-Type': 'application/json' } }
        )
        alert(ConstList.REGISTER_MESSAGE)
        this.showModal = false
        this.$router.push({ name: ConstList.ROUTE_CLIENT_LIST })
      } catch (error) {
        this.updateError = '取引先情報を更新できませんでした。時間をおいて再度お試しください。'
        console.error('クライアント更新失敗:', safeErrorDigest(error))
      } finally {
        this.loading = false
      }
    },
    closeModal () {
      // 送っているあいだは閉じない（2026-09-26 監査）。閉じると、あとから届いた理由を出す場所がなくなる。
      if (this.loading) return
      this.showModal = false
      this.updateError = ''
    }
  }
}
</script>

<style scoped>
button:disabled {
  cursor: not-allowed;
}
</style>
